import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Flame,
  Thermometer,
  Snowflake,
  Phone,
  Mail,
  Building2,
  Users,
  DollarSign,
  MessageSquare,
  PhoneCall,
  Video,
  CheckSquare,
  Plus,
  Check,
  Clock,
  AlertCircle,
  Package,
  Trash2,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";

import {
  getLead,
  updateLead,
  deleteLead,
  createActivity,
  completeTask,
  getProducts,
  type LeadStage,
  type LeadWarmth,
  type LeadUpdate,
  type ActivityType,
  type ActivityCreate,
} from "@/lib/api";

const STAGE_LABELS: Record<LeadStage, string> = {
  new: "New",
  contacted: "Contacted",
  demo_scheduled: "Demo Scheduled",
  demo_done: "Demo Done",
  negotiation: "Negotiation",
  won: "Won",
  lost: "Lost",
};

const STAGE_ORDER: LeadStage[] = [
  "new",
  "contacted",
  "demo_scheduled",
  "demo_done",
  "negotiation",
  "won",
  "lost",
];

const ACTIVITY_ICONS: Record<ActivityType, React.ReactNode> = {
  note: <MessageSquare className="h-4 w-4" />,
  call: <PhoneCall className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  meeting: <Video className="h-4 w-4" />,
  task: <CheckSquare className="h-4 w-4" />,
  demo: <Video className="h-4 w-4 text-purple-500" />,
};

const ACTIVITY_LABELS: Record<ActivityType, string> = {
  note: "Note",
  call: "Call",
  email: "Email",
  meeting: "Meeting",
  task: "Task",
  demo: "Demo",
};

const WarmthIcon = ({ warmth }: { warmth: LeadWarmth }) => {
  switch (warmth) {
    case "hot":
      return <Flame className="h-5 w-5 text-red-500" />;
    case "warm":
      return <Thermometer className="h-5 w-5 text-orange-500" />;
    case "cold":
      return <Snowflake className="h-5 w-5 text-blue-500" />;
  }
};

export function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isAddActivityOpen, setIsAddActivityOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [newActivity, setNewActivity] = useState<ActivityCreate>({
    lead_id: id,
    type: "note",
    title: "",
    content: "",
  });

  const {
    data: lead,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["lead", id],
    queryFn: () => getLead(id!),
    enabled: !!id,
  });

  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: () => getProducts(),
  });

  const updateMutation = useMutation({
    mutationFn: (data: LeadUpdate) => updateLead(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", id] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });

  const addActivityMutation = useMutation({
    mutationFn: createActivity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", id] });
      setIsAddActivityOpen(false);
      setNewActivity({ lead_id: id, type: "note", title: "", content: "" });
    },
  });

  const completeTaskMutation = useMutation({
    mutationFn: completeTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteLead(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      navigate("/leads");
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
        Lead not found
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/leads")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <WarmthIcon warmth={lead.warmth} />
            <h1 className="text-2xl font-bold">{lead.name}</h1>
          </div>
          {lead.company && (
            <p className="text-muted-foreground flex items-center gap-1">
              <Building2 className="h-4 w-4" />
              {lead.company}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIsAddActivityOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Activity
          </Button>
          <Button
            variant="destructive"
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Stage Pipeline */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2">
            {STAGE_ORDER.map((stage) => (
              <button
                key={stage}
                onClick={() => updateMutation.mutate({ stage })}
                disabled={updateMutation.isPending}
                className={`flex-1 py-2 px-3 text-sm rounded-lg transition-colors ${
                  lead.stage === stage
                    ? "bg-primary text-primary-foreground"
                    : stage === "won"
                      ? "bg-green-100 text-green-800 hover:bg-green-200"
                      : stage === "lost"
                        ? "bg-red-100 text-red-800 hover:bg-red-200"
                        : "bg-muted hover:bg-muted/80"
                }`}
              >
                {STAGE_LABELS[stage]}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left: Info */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <a href={`mailto:${lead.email}`} className="hover:underline">
                  {lead.email}
                </a>
              </div>
              {lead.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${lead.phone}`} className="hover:underline">
                    {lead.phone}
                  </a>
                </div>
              )}
              {lead.team_size && (
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  {lead.team_size} people
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Deal info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Warmth</label>
                <Select
                  value={lead.warmth}
                  onValueChange={(v) =>
                    updateMutation.mutate({ warmth: v as LeadWarmth })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hot">Hot</SelectItem>
                    <SelectItem value="warm">Warm</SelectItem>
                    <SelectItem value="cold">Cold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">
                  Expected Value
                </label>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={lead.expected_value || ""}
                    onChange={(e) =>
                      updateMutation.mutate({
                        expected_value: e.target.value || undefined,
                      })
                    }
                    placeholder="0"
                  />
                  <span className="text-muted-foreground">Kc</span>
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">
                  Probability
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={lead.probability}
                    onChange={(e) =>
                      updateMutation.mutate({
                        probability: Number(e.target.value),
                      })
                    }
                  />
                  <span className="text-muted-foreground">%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Products
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {products.map((product) => {
                  const isSelected = lead.products?.some(
                    (p) => p.id === product.id,
                  );
                  return (
                    <button
                      key={product.id}
                      onClick={() => {
                        const currentIds =
                          lead.products?.map((p) => p.id) || [];
                        const newIds = isSelected
                          ? currentIds.filter((pId) => pId !== product.id)
                          : [...currentIds, product.id];
                        updateMutation.mutate({ product_ids: newIds });
                      }}
                      disabled={updateMutation.isPending}
                      className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-muted/80"
                      }`}
                    >
                      {product.name}
                    </button>
                  );
                })}
              </div>
              {products.length === 0 && (
                <p className="text-muted-foreground text-sm">
                  No products available
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Activity Timeline */}
        <div className="col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {lead.activities && lead.activities.length > 0 ? (
                <div className="space-y-4">
                  {lead.activities.map((activity) => (
                    <div
                      key={activity.id}
                      className={`flex gap-4 p-4 rounded-lg border ${
                        activity.is_overdue ? "border-red-200 bg-red-50" : ""
                      }`}
                    >
                      <div className="mt-1">
                        {ACTIVITY_ICONS[activity.type]}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{activity.title}</span>
                          <span className="text-sm text-muted-foreground">
                            {formatDistanceToNow(
                              new Date(activity.created_at),
                              {
                                addSuffix: true,
                              },
                            )}
                          </span>
                        </div>
                        {activity.content && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {activity.content}
                          </p>
                        )}
                        {activity.type === "task" && (
                          <div className="flex items-center gap-2 mt-2">
                            {activity.completed_at ? (
                              <Badge
                                variant="outline"
                                className="text-green-600"
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Done
                              </Badge>
                            ) : (
                              <>
                                {activity.is_overdue && (
                                  <Badge variant="destructive">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    Overdue
                                  </Badge>
                                )}
                                {activity.due_date && (
                                  <Badge variant="outline">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {format(
                                      new Date(activity.due_date),
                                      "d. M. yyyy",
                                    )}
                                  </Badge>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    completeTaskMutation.mutate(activity.id)
                                  }
                                  disabled={completeTaskMutation.isPending}
                                >
                                  <Check className="h-3 w-3 mr-1" />
                                  Complete
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No activity yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Activity Dialog */}
      <Dialog open={isAddActivityOpen} onOpenChange={setIsAddActivityOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Activity</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Type</Label>
              <Select
                value={newActivity.type}
                onValueChange={(v) =>
                  setNewActivity({ ...newActivity, type: v as ActivityType })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ACTIVITY_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Title *</Label>
              <Input
                value={newActivity.title}
                onChange={(e) =>
                  setNewActivity({ ...newActivity, title: e.target.value })
                }
                placeholder="e.g. Called them..."
              />
            </div>
            <div className="grid gap-2">
              <Label>Content</Label>
              <Textarea
                value={newActivity.content || ""}
                onChange={(e) =>
                  setNewActivity({ ...newActivity, content: e.target.value })
                }
                placeholder="Details..."
                rows={3}
              />
            </div>
            {newActivity.type === "task" && (
              <div className="grid gap-2">
                <Label>Due Date</Label>
                <Input
                  type="datetime-local"
                  value={newActivity.due_date || ""}
                  onChange={(e) =>
                    setNewActivity({ ...newActivity, due_date: e.target.value })
                  }
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddActivityOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => addActivityMutation.mutate(newActivity)}
              disabled={!newActivity.title || addActivityMutation.isPending}
            >
              {addActivityMutation.isPending ? "Saving..." : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat lead?</AlertDialogTitle>
            <AlertDialogDescription>
              Tato akce je nevratná. Lead "{lead.name}" a všechny jeho aktivity
              budou trvale smazány.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Mažu..." : "Smazat"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
